import { defineStore } from 'pinia';
import api, { apiClient } from '@/services/api';

export const useAuthStore = defineStore('auth', {
  state: () => {
    // Check localStorage on store initialization
    const storedToken = localStorage.getItem('access_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    const storedUser = localStorage.getItem('user');
    
    console.log('🔍 Auth Store Initialization:');
    console.log('  - Stored access token:', storedToken ? 'Present' : 'Missing');
    console.log('  - Stored refresh token:', storedRefreshToken ? 'Present' : 'Missing');
    console.log('  - Stored user:', storedUser ? 'Present' : 'Missing');
    
    return {
      user: storedUser ? JSON.parse(storedUser) : null,
      accessToken: storedToken,
      refreshToken: storedRefreshToken,
      loading: false,
      error: null
    };
  },

  getters: {
    isAuthenticated: (state) => {
      console.log('isAuthenticated check:', !!state.accessToken, 'token:', state.accessToken);
      return !!state.accessToken;
    },
    isAdmin: (state) => {
      console.log('isAdmin check:', state.user, 'is_staff:', state.user?.is_staff, 'role:', state.user?.role);
      return state.user?.is_staff === true || state.user?.role === 'admin';
    },
    isAuthor: (state) => {
      console.log('isAuthor check:', state.user, 'role:', state.user?.role);
      return state.user?.role === 'author';
    },
    isEditor: (state) => {
      console.log('isEditor check:', state.user, 'role:', state.user?.role);
      return state.user?.role === 'editor';
    }
  },

  actions: {
    async login(email, password) {
      this.loading = true;
      this.error = null;

      try {
        console.log('Starting login process for:', email);
        
        // First, get the tokens
        const response = await api.login({ email, password });
        console.log('Login response:', response.data);
        
        this.accessToken = response.data.access;
        this.refreshToken = response.data.refresh;

        // Store tokens
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);

        // Set up authorization header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;

        // Wait a moment before fetching profile
        await new Promise(resolve => setTimeout(resolve, 500));

        // Then, fetch the user profile
        try {
          const userResponse = await api.getUserProfile();
          console.log('User profile response:', userResponse.data);
          console.log('User role from API:', userResponse.data.role);
          console.log('User is_staff from API:', userResponse.data.is_staff);
          
          this.user = {
            ...userResponse.data,
            is_staff: userResponse.data.is_staff === true
          };
          
          console.log('Final user state:', this.user);
          console.log('User role in state:', this.user.role);
          console.log('User is_staff in state:', this.user.is_staff);
        } catch (profileError) {
          console.error('Profile fetch error:', profileError);
          console.error('Profile fetch error response:', profileError.response);
          throw profileError;
        }

        return true;
      } catch (error) {
        console.error('Login process error:', error);
        console.error('Error response:', error.response);
        this.error = error.response?.data?.detail || 'Login failed';
        return false;
      } finally {
        this.loading = false;
      }
    },

    async fetchUserProfile() {
      if (!this.accessToken) {
        console.log('No access token available');
        return null;
      }

      this.loading = true;

      try {
        // Ensure authorization header is set
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${this.accessToken}`;
        
        console.log('Fetching user profile with token:', this.accessToken);
        const response = await api.getUserProfile();
        console.log('Fetched user profile response:', response.data);
        console.log('User role from API:', response.data.role);
        console.log('User is_staff from API:', response.data.is_staff);
        
        this.user = {
          ...response.data,
          is_staff: response.data.is_staff === true
        };
        
        console.log('Updated user state:', this.user);
        console.log('User role in state:', this.user.role);
        console.log('User is_staff in state:', this.user.is_staff);
        return this.user;
      } catch (error) {
        console.error('Error fetching user profile:', error);
        console.error('Error response:', error.response);
        this.error = error.response?.data?.detail || 'Failed to fetch user profile';
        return null;
      } finally {
        this.loading = false;
      }
    },

    async register(userData) {
      this.loading = true;
      this.error = null;

      try {
        await api.register(userData);
        return { success: true };
      } catch (error) {
        console.error('Registration error:', error);
        console.error('Error response:', error.response);
        
        // Handle different types of errors
        if (error.response?.status === 400) {
          // Validation errors - return field-specific errors
          const validationErrors = error.response.data;
          console.log('Validation errors:', validationErrors);
          return { 
            success: false, 
            errors: validationErrors,
            message: 'Please fix the errors below'
          };
        } else if (error.response?.status === 409) {
          // Conflict errors (duplicate email/username)
          return { 
            success: false, 
            errors: error.response.data,
            message: 'User already exists'
          };
        } else {
          // Generic error
          this.error = error.response?.data?.detail || 'Registration failed';
          return { 
            success: false, 
            errors: { general: this.error },
            message: this.error
          };
        }
      } finally {
        this.loading = false;
      }
    },

    logout() {
      // Clear auth header
      delete apiClient.defaults.headers.common['Authorization'];
      
      // Clear state
      this.user = null;
      this.accessToken = null;
      this.refreshToken = null;
      this.loading = false;
      
      // Clear storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },

  persist: {
    storage: localStorage,
    paths: ['accessToken', 'refreshToken', 'user']
  }
});